import { ColumnApi } from "ag-grid-community";
import { TotalTypes, ICurrency, IAllocationReferenceFields, PlanMetricRows } from "./pinned-row-constants";
import { Kinds } from "constants/product-collection";
import get from "lodash/get";
import isNumber from "lodash/isNumber";
import { IDoorTotals, IDoorTotal } from "reducers/collection";
import { formatNumber } from "lib/globalize-util";
import { ColumnTypes } from "components/product-collection/product-collection-views/product-collection-grid-view/product-collection-grid-view-helper";
import { safeParseFloat } from "lib/number";
import { DIMENSION_IDS_DELIMITER } from "epics/collections/review-plans";
import { BLANK_CLASSIFICATION_VALUE, IDoor } from "helpers/door-collection";
import { amphibiousGetIn } from "helpers/objUtils";
import { ISortOrder, ISortSortBy } from "../../tool-panels";
import { IDoorGroupMetricName, IDoorMetricsTotal, readDoorMetrics } from "helpers/server-rollup";

const generateTotalRows = (
    pinnedRowSorts?: {
        id: string;
        order: ISortOrder;
        sort_by: ISortSortBy;
    }[],
    onClick?: (index: number) => void,
): object[] => {
    const rows = [];
    for (let i = 0; i < Object.keys(TotalTypes).length; i++) {
        rows.push({
            pinnedRowSorts,
            totalType: getTotalType(i),
            onClick: () => (onClick ? onClick(i) : undefined),
        });
    }
    return rows;
};

export interface ITotalRowResponse {
    doorMetricsTotal: IDoorMetricsTotal;
    doorGroups?: IDoorGroupMetricName[];
    hasDeliveries?: boolean;
}

export const getServerRollupAssortmentTotalsRow = ({
    doorMetricsTotal,
    doorGroups,
    hasDeliveries,
}: ITotalRowResponse) => {
    const { total_metrics, group_metrics } = doorMetricsTotal;
    const { totalMetrics, doorMetrics } = readDoorMetrics(
        {
            group_metrics,
            total_metrics,
        },
        doorGroups,
        hasDeliveries,
    );

    const totalRow = {
        totalType: ASSORTMENT_TOTALS_ROW,
        onClick: () => {},
        totalMetrics,
        doorMetrics,
    };
    return [totalRow];
};

export const ASSORTMENT_TOTALS_ROW = "ASSORTMENT_TOTALS_ROW";
export const getAssortmentTotalsRow = () => {
    const totalRow = {
        totalType: ASSORTMENT_TOTALS_ROW,
        onClick: () => {},
    };
    return [totalRow];
};

export const getPinnedRows = (
    showTotals: boolean,
    dimensionPlanMetrics: string[] = [],
    accessPlan: boolean,
    rowData: object = {},
): object[] | undefined => {
    let pinnedRows = [];
    const displayPlanMetrics = accessPlan && dimensionPlanMetrics.length;
    const displayTotals = showTotals && !displayPlanMetrics;
    if (displayPlanMetrics) {
        dimensionPlanMetrics.forEach((totalType) => {
            const rows = Object.keys(PlanMetricRows).map((planType, index) => {
                return {
                    plan: rowData,
                    planType,
                    totalType,
                };
            });
            pinnedRows.push(...rows);
        });
    } else if (displayTotals) {
        pinnedRows = generateTotalRows();
    }
    return pinnedRows;
};

export const validatePinnedRows = (
    showTotals: boolean,
    accessPlan: boolean,
    rows: any[],
    pinnedRowSorts?: {
        id: string;
        order: ISortOrder;
        sort_by: ISortSortBy;
    }[],
    onClick?: (index: number) => void,
) => {
    const displayPlanMetrics = accessPlan && rows[0]?.plan;
    const displayTotals = showTotals && !displayPlanMetrics;
    if (displayPlanMetrics) {
        const finalRows = rows.map((row, index) => {
            return {
                ...row,
                pinnedRowSorts,
                onClick: () => (onClick ? onClick(index) : undefined),
            };
        });
        return finalRows;
    } else if (displayTotals) {
        return generateTotalRows(pinnedRowSorts, onClick);
    } else {
        return [];
    }
};

export const isCurrency = (totalType: TotalTypes): boolean =>
    ![TotalTypes.Units, TotalTypes.Skus, TotalTypes.Depth].includes(totalType);

const getReferenceField = (totalTypes: TotalTypes, allocationReferenceFields: IAllocationReferenceFields) => {
    switch (totalTypes) {
        case TotalTypes.Retail:
            return allocationReferenceFields.retailFieldName;
        case TotalTypes.AvgRetail:
            return allocationReferenceFields.retailFieldName;
        case TotalTypes.Wholesale:
            return allocationReferenceFields.wholesaleFieldName;
        case TotalTypes.AvgWholesale:
            return allocationReferenceFields.wholesaleFieldName;
        default:
            return;
    }
};

export const getCurrency = (
    totalTypes: TotalTypes,
    allocationReferenceFields: IAllocationReferenceFields,
    columnApi: ColumnApi,
): ICurrency => {
    let currency: ICurrency;
    const allocationReferenceField = getReferenceField(totalTypes, allocationReferenceFields);
    if (allocationReferenceField) {
        const column = columnApi.getColumn(allocationReferenceField);
        if (column) {
            currency = get(column.getColDef(), ["cellEditorParams", "currency"]);
        }
    }
    return currency;
};

export const setCurrency = (
    totalType: TotalTypes,
    allocationReferenceFields: IAllocationReferenceFields,
    columnApi: ColumnApi,
    hasDoors: boolean,
    kind: Kinds,
    merchantCurrency: ICurrency,
): ICurrency => {
    let currency: ICurrency;
    if ([Kinds.TOTAL_ALLOCATION_DIMENSIONS, Kinds.DOOR_BULK_QUANTITY].includes(kind) && hasDoors) {
        currency = merchantCurrency;
    } else {
        currency = getCurrency(totalType as TotalTypes, allocationReferenceFields, columnApi);
    }
    return currency;
};

export const getValueBasedOnTotalType = (doorTotals: IDoorTotals | IDoorTotal, path: string[]) => {
    const totalType = path.pop();
    let value = 0;
    if (totalType === TotalTypes.Skus) {
        const skus = get(doorTotals, [...path, totalType], {});
        value = isNumber(skus) ? skus : Object.keys(skus).length;
    } else if (totalType === TotalTypes.Depth) {
        const units = get(doorTotals, [...path, TotalTypes.Units], 0);
        const skusInput = get(doorTotals, [...path, TotalTypes.Skus], {});
        const skus = isNumber(skusInput) ? skusInput : Object.keys(skusInput).length;
        value = skus > 0 ? units / skus : 0;
    } else if (totalType === TotalTypes.AvgWholesale) {
        const units = get(doorTotals, [...path, TotalTypes.Units], 0);
        const wholesale = get(doorTotals, [...path, TotalTypes.Wholesale], 0);
        const averageWholesale = units > 0 ? wholesale / units : 0;
        value = isNaN(averageWholesale) ? 0 : averageWholesale;
    } else if (totalType === TotalTypes.AvgRetail) {
        const units = get(doorTotals, [...path, TotalTypes.Units], 0);
        const retail = get(doorTotals, [...path, TotalTypes.Retail], 0);
        const averageRetail = units > 0 ? retail / units : 0;
        value = isNaN(averageRetail) ? 0 : averageRetail;
    } else {
        value = get(doorTotals, [...path, totalType], 0);
    }
    return value;
};

export const buildPathBasedOnType = (doorId: string, groupId?: string): string[] => {
    let path = [];
    if (groupId) {
        path = [doorId, "deliveries", groupId];
    } else {
        path = [doorId];
    }
    return path;
};

export const getTotalType = (rowIndex: number, plans?: string[]): TotalTypes => {
    if (plans) {
        const planMetricRowsLength = Object.keys(PlanMetricRows).length;
        const planIndex = Math.floor(rowIndex / planMetricRowsLength);
        return plans[planIndex] as TotalTypes;
    } else {
        switch (rowIndex) {
            case 0:
                return TotalTypes.Units;
            case 1:
                return TotalTypes.Skus;
            case 2:
                return TotalTypes.Depth;
            case 3:
                return TotalTypes.Wholesale;
            case 4:
                return TotalTypes.Retail;
            case 5:
                return TotalTypes.AvgWholesale;
            case 6:
                return TotalTypes.AvgRetail;
            default:
                return;
        }
    }
};

export const getIndexOnTotalType = (totalType: TotalTypes, plans: string[]): number => {
    const planIndex = plans.indexOf(totalType);
    const planMetricRowsLength = Object.keys(PlanMetricRows).length;
    return planIndex * planMetricRowsLength;
};

export const getPlanRowType = (rowIndex: number): PlanMetricRows => {
    const planMetricRowsLength = Object.keys(PlanMetricRows).length;
    switch (rowIndex % planMetricRowsLength) {
        case 0:
            return PlanMetricRows.Plan;
        case 1:
            return PlanMetricRows.Total;
        case 2:
            return PlanMetricRows.Diff;
        default:
            return;
    }
};

interface TotalClassificationTotalParams {
    colType: ColumnTypes.ClassificationTotal;
    doorTotals: IDoorTotals;
    groupId: string;
    totalType: TotalTypes;
    classificationName: string;
    classificationValue: string;
}
interface TotalAllocationDimensionTotalParams {
    colType: ColumnTypes.AllocationDimensionTotal;
    doorTotals: IDoorTotals;
    groupId: string;
    totalType: TotalTypes;
}
interface TotalDoorBulkQuantityParams {
    colType: ColumnTypes.DoorBulkQuantity;
    doorTotals: IDoorTotals;
    groupId: string;
    doorId: string;
    totalType: TotalTypes;
}
interface TotalAllDimensionTotalParams {
    colType: ColumnTypes.AllDimensionTotal;
    doorTotals: IDoorTotals;
    totalType: TotalTypes;
}
interface TotalNestedDimensionTotalParams {
    colType: ColumnTypes.NestedDimensionTotal;
    doorTotals: IDoorTotals;
    totalType: TotalTypes;
    doorId: string;
}

// This function returns the value for door totals view based on the columnType
export const getTotal = (
    params:
        | TotalClassificationTotalParams
        | TotalAllocationDimensionTotalParams
        | TotalDoorBulkQuantityParams
        | TotalAllDimensionTotalParams
        | TotalNestedDimensionTotalParams,
): number => {
    const { doorTotals, colType, totalType } = params;
    let actualValue;
    if (colType === ColumnTypes.ClassificationTotal) {
        const { groupId, classificationName, classificationValue } = params as TotalClassificationTotalParams;
        const filteredDoors = Object.keys(doorTotals || {}).filter((door) => {
            const _classificationValue =
                classificationValue === BLANK_CLASSIFICATION_VALUE ? undefined : classificationValue;
            return _classificationValue
                ? door !== "_total" &&
                      get(doorTotals, [door, "classifications", classificationName]) === _classificationValue
                : door !== "_total" && !get(doorTotals, [door, "classifications", classificationName]);
        });

        const { units, retail, wholesale } = filteredDoors.reduce(
            (sum, door) => {
                if (groupId) {
                    return {
                        units:
                            sum.units +
                            getValueBasedOnTotalType(doorTotals, [door, "deliveries", groupId, TotalTypes.Units]),
                        retail:
                            sum.retail +
                            getValueBasedOnTotalType(doorTotals, [door, "deliveries", groupId, TotalTypes.Retail]),
                        wholesale:
                            sum.wholesale +
                            getValueBasedOnTotalType(doorTotals, [door, "deliveries", groupId, TotalTypes.Wholesale]),
                    };
                }
                return {
                    units: sum.units + getValueBasedOnTotalType(doorTotals, [door, TotalTypes.Units]),
                    retail: sum.retail + getValueBasedOnTotalType(doorTotals, [door, TotalTypes.Retail]),
                    wholesale: sum.wholesale + getValueBasedOnTotalType(doorTotals, [door, TotalTypes.Wholesale]),
                };
            },
            { units: 0, retail: 0, wholesale: 0 },
        );

        if ([TotalTypes.Skus, TotalTypes.Depth].includes(totalType)) {
            const isSkuANumber = isNumber(
                groupId
                    ? get(doorTotals, [filteredDoors[0], "deliveries", groupId, TotalTypes.Skus], {})
                    : get(doorTotals, [filteredDoors[0], TotalTypes.Skus], {}),
            );

            const skuIds = filteredDoors.reduce(
                (sum, door) => {
                    if (groupId) {
                        const skuNode = get(doorTotals, [door, "deliveries", groupId, TotalTypes.Skus], {});
                        if (isSkuANumber) {
                            return sum + skuNode;
                        }

                        return {
                            ...sum,
                            ...skuNode,
                        };
                    }

                    const skuNode = get(doorTotals, [door, TotalTypes.Skus], {});
                    if (isSkuANumber) {
                        return (sum as number) + (skuNode as number);
                    }
                    return {
                        ...sum,
                        ...(skuNode as object),
                    };
                },
                isSkuANumber ? {} : 0,
            );
            const skus = Object.keys(skuIds).length;
            if (totalType === TotalTypes.Skus) {
                actualValue = skus;
            } else if (totalType === TotalTypes.Depth) {
                actualValue = skus > 0 ? units / skus : 0;
            }
        } else if (totalType === TotalTypes.AvgRetail) {
            actualValue = units > 0 ? retail / units : 0;
        } else if (totalType === TotalTypes.AvgWholesale) {
            actualValue = units > 0 ? wholesale / units : 0;
        } else if (totalType === TotalTypes.Retail) {
            actualValue = retail;
        } else if (totalType === TotalTypes.Wholesale) {
            actualValue = wholesale;
        } else if (totalType === TotalTypes.Units) {
            actualValue = units;
        }
    } else if (colType === ColumnTypes.AllocationDimensionTotal) {
        const { groupId } = params as TotalAllocationDimensionTotalParams;
        if (groupId) {
            actualValue = getValueBasedOnTotalType(doorTotals, ["_total", "deliveries", groupId, totalType]);
        } else {
            actualValue = getValueBasedOnTotalType(doorTotals, ["_total", totalType]);
        }
    } else if (colType === ColumnTypes.AllDimensionTotal) {
        let totalValue = 0;
        if (doorTotals?._total) {
            const totalUnits = doorTotals._total[TotalTypes.Units];
            const skuInput = doorTotals._total[TotalTypes.Skus];

            if (totalType === TotalTypes.Skus) {
                totalValue = isNumber(skuInput) ? skuInput : Object.keys(skuInput).length;
            } else if (totalType === TotalTypes.Depth) {
                const totalProducts = isNumber(skuInput) ? skuInput : Object.keys(skuInput).length;
                totalValue = totalProducts > 0 ? totalUnits / totalProducts : 0;
            } else if (totalType === TotalTypes.AvgRetail) {
                totalValue = totalUnits > 0 ? doorTotals._total[TotalTypes.Retail] / totalUnits : 0;
            } else if (totalType === TotalTypes.AvgWholesale) {
                totalValue = totalUnits > 0 ? doorTotals._total[TotalTypes.Wholesale] / totalUnits : 0;
            } else {
                totalValue = doorTotals._total[totalType];
            }
        }
        actualValue = totalValue;
    } else if (colType === ColumnTypes.DoorBulkQuantity) {
        const { groupId, doorId } = params as TotalDoorBulkQuantityParams;
        if (groupId) {
            actualValue = getValueBasedOnTotalType(doorTotals, [doorId, "deliveries", groupId, totalType]);
        } else {
            actualValue = getValueBasedOnTotalType(doorTotals, [doorId, totalType]);
        }
    } else if (colType === ColumnTypes.NestedDimensionTotal) {
        const { doorId } = params as TotalNestedDimensionTotalParams;
        const doorDeliveries = get(doorTotals, [doorId, "deliveries"], {});
        const { units, retail, wholesale, skus } = Object.keys(doorDeliveries).reduce(
            (sum, deliveryKey) => {
                const skuInput = doorDeliveries[deliveryKey].skus;
                const skuValue = isNumber(skuInput) ? skuInput : Object.keys(skuInput).length;
                return {
                    units: sum.units + doorDeliveries[deliveryKey].units,
                    retail: sum.retail + doorDeliveries[deliveryKey].retail,
                    wholesale: sum.wholesale + doorDeliveries[deliveryKey].wholesale,
                    skus: Math.max(sum.skus, skuValue),
                };
            },
            { units: 0, retail: 0, wholesale: 0, skus: 0 },
        );

        if ([TotalTypes.Skus, TotalTypes.Depth].includes(totalType)) {
            if (totalType === TotalTypes.Skus) {
                actualValue = skus;
            } else if (totalType === TotalTypes.Depth) {
                actualValue = skus > 0 ? units / skus : 0;
            }
        } else if (totalType === TotalTypes.AvgRetail) {
            actualValue = units > 0 ? retail / units : 0;
        } else if (totalType === TotalTypes.AvgWholesale) {
            actualValue = units > 0 ? wholesale / units : 0;
        } else if (totalType === TotalTypes.Retail) {
            actualValue = retail;
        } else if (totalType === TotalTypes.Wholesale) {
            actualValue = wholesale;
        } else if (totalType === TotalTypes.Units) {
            actualValue = units;
        }
    }

    return actualValue || 0;
};

interface PlanClassificationTotalParams {
    colType: ColumnTypes.ClassificationTotal;
    doorTotals: IDoorTotals;
    doors: IDoor[];
    groupId: string;
    totalType: TotalTypes;
    classificationName: string;
    classificationValue: string;
    reviewPlan: any;
}
interface PlanAllocationDimensionTotalParams {
    colType: ColumnTypes.AllocationDimensionTotal;
    doorTotals: IDoorTotals;
    doors: IDoor[];
    groupId: string;
    totalType: TotalTypes;
    reviewPlan: any;
    dimensionFilters: string[][];
}
interface PlanDoorBulkQuantityParams {
    colType: ColumnTypes.DoorBulkQuantity;
    doorTotals: IDoorTotals;
    groupId?: string;
    doorId: string;
    totalType: TotalTypes;
    reviewPlan: any;
}
interface PlanAllDimensionTotalParams {
    colType: ColumnTypes.AllDimensionTotal;
    doorTotals: IDoorTotals;
    totalType: TotalTypes;
    reviewPlan: any;
    doors: IDoor[];
    groupId?: string;
}
interface PlanNestedDimensionTotalParams {
    colType: ColumnTypes.NestedDimensionTotal;
    totalType: TotalTypes;
    doorId: string;
    reviewPlan: any;
}
export const getPlan = (
    params:
        | PlanClassificationTotalParams
        | PlanAllocationDimensionTotalParams
        | PlanDoorBulkQuantityParams
        | PlanAllDimensionTotalParams
        | PlanNestedDimensionTotalParams,
): number => {
    const { colType, reviewPlan, totalType } = params;
    let plan = 0;

    if (colType === ColumnTypes.DoorBulkQuantity) {
        const { doorId, groupId } = params as PlanDoorBulkQuantityParams;
        const planPath = buildPathBasedOnType(doorId, groupId).join(DIMENSION_IDS_DELIMITER);
        const planValue = amphibiousGetIn(reviewPlan, [planPath, "struct_value", totalType], "");

        plan = safeParseFloat(planValue, 0);
    } else if (colType === ColumnTypes.NestedDimensionTotal) {
        const { doorId } = params as PlanNestedDimensionTotalParams;
        const { units, retail, wholesale, skus } = Object.keys(reviewPlan).reduce(
            (sum, key) => {
                const reviewItem = reviewPlan[key];
                const doorKey = key.split(DIMENSION_IDS_DELIMITER)[0];
                if (doorId === doorKey) {
                    return {
                        units: sum.units + safeParseFloat(amphibiousGetIn(reviewItem, ["struct_value", "units"]), 0),
                        retail: sum.retail + safeParseFloat(amphibiousGetIn(reviewItem, ["struct_value", "retail"]), 0),
                        wholesale:
                            sum.wholesale +
                            safeParseFloat(amphibiousGetIn(reviewItem, ["struct_value", "wholesale"]), 0),
                        skus: Math.max(sum.skus, amphibiousGetIn(reviewItem, ["struct_value", "skus"])),
                    };
                }

                return sum;
            },
            { units: 0, retail: 0, wholesale: 0, skus: 0 },
        );

        if ([TotalTypes.Skus, TotalTypes.Depth].includes(totalType)) {
            if (totalType === TotalTypes.Skus) {
                plan = skus;
            } else if (totalType === TotalTypes.Depth) {
                plan = skus > 0 ? units / skus : 0;
            }
        } else if (totalType === TotalTypes.AvgRetail) {
            plan = units > 0 ? retail / units : 0;
        } else if (totalType === TotalTypes.AvgWholesale) {
            plan = units > 0 ? wholesale / units : 0;
        } else if (totalType === TotalTypes.Retail) {
            plan = retail;
        } else if (totalType === TotalTypes.Wholesale) {
            plan = wholesale;
        } else if (totalType === TotalTypes.Units) {
            plan = units;
        }
    } else {
        const { doorTotals, doors, groupId } = params as
            | PlanClassificationTotalParams
            | PlanAllocationDimensionTotalParams
            | PlanAllDimensionTotalParams;
        let filteredDoors = [];
        if (colType === ColumnTypes.ClassificationTotal) {
            const { classificationName, classificationValue } = params as PlanClassificationTotalParams;
            filteredDoors = Object.keys(doorTotals || {}).filter((door) => {
                const _classificationValue =
                    classificationValue === BLANK_CLASSIFICATION_VALUE ? undefined : classificationValue;
                return _classificationValue
                    ? door !== "_total" &&
                          get(doorTotals, [door, "classifications", classificationName]) === _classificationValue
                    : door !== "_total" && !get(doorTotals, [door, "classifications", classificationName]);
            });
        } else if (colType === ColumnTypes.AllocationDimensionTotal) {
            const { dimensionFilters } = params as PlanAllocationDimensionTotalParams;
            const [_allocationFilter, doorFilter] = dimensionFilters;
            filteredDoors = Object.keys(doorTotals || {}).filter((door) => {
                const notTotal = door !== "_total";
                const matchingFilter = doorFilter && doorFilter.length ? doorFilter.includes(door) : true;
                return notTotal && matchingFilter;
            });
        } else if (colType === ColumnTypes.AllDimensionTotal) {
            const { allUnits, allRetail, allWholesale, allSkus } = Object.keys(reviewPlan).reduce(
                (planValueTotal, key) => {
                    const reviewPlanItem = reviewPlan[key];
                    const door = doors?.find((door) => door.id === key.split(DIMENSION_IDS_DELIMITER)[0]);
                    const totalConversionRate = getTotalConversionRate(door);
                    return {
                        allUnits:
                            planValueTotal.allUnits +
                            safeParseFloat(amphibiousGetIn(reviewPlanItem, ["struct_value", TotalTypes.Units], ""), 0),
                        allRetail:
                            planValueTotal.allRetail +
                            safeParseFloat(
                                amphibiousGetIn(reviewPlanItem, ["struct_value", TotalTypes.Retail], "") /
                                    totalConversionRate,
                                0,
                            ),
                        allWholesale:
                            planValueTotal.allWholesale +
                            safeParseFloat(
                                amphibiousGetIn(reviewPlanItem, ["struct_value", TotalTypes.Wholesale], "") /
                                    totalConversionRate,
                                0,
                            ),
                        allSkus: Math.max(
                            planValueTotal.allSkus,
                            safeParseFloat(amphibiousGetIn(reviewPlanItem, ["struct_value", TotalTypes.Skus], ""), 0),
                        ),
                    };
                },
                {
                    allUnits: 0,
                    allRetail: 0,
                    allWholesale: 0,
                    allSkus: 0,
                },
            );

            switch (totalType) {
                case TotalTypes.Units:
                    return allUnits;
                case TotalTypes.Skus:
                    return allSkus;
                case TotalTypes.Depth:
                    return allSkus > 0 ? allUnits / allSkus : 0;
                case TotalTypes.Retail:
                    return allRetail;
                case TotalTypes.Wholesale:
                    return allWholesale;
                case TotalTypes.AvgRetail:
                    return allUnits > 0 ? allRetail / allUnits : 0;
                case TotalTypes.AvgWholesale:
                    return allUnits > 0 ? allWholesale / allUnits : 0;
                default:
                    return 0;
            }
        }

        if ([TotalTypes.AvgRetail, TotalTypes.AvgWholesale].includes(totalType)) {
            const { avgUnits, avgRetail, avgWholesale } = filteredDoors.reduce(
                (planValueTotal, door) => {
                    const doorObject = doors?.find((doorObj) => doorObj.id === door);
                    const totalConversionRate = getTotalConversionRate(doorObject);
                    const planPath = buildPathBasedOnType(door, groupId).join(DIMENSION_IDS_DELIMITER);
                    return {
                        avgUnits:
                            planValueTotal.avgUnits +
                            safeParseFloat(
                                amphibiousGetIn(reviewPlan, [planPath, "struct_value", TotalTypes.Units], ""),
                                0,
                            ),
                        avgRetail:
                            planValueTotal.avgRetail +
                            safeParseFloat(
                                amphibiousGetIn(reviewPlan, [planPath, "struct_value", TotalTypes.Retail], "") /
                                    totalConversionRate,
                                0,
                            ),
                        avgWholesale:
                            planValueTotal.avgWholesale +
                            safeParseFloat(
                                amphibiousGetIn(reviewPlan, [planPath, "struct_value", TotalTypes.Wholesale], "") /
                                    totalConversionRate,
                                0,
                            ),
                    };
                },
                {
                    avgUnits: 0,
                    avgRetail: 0,
                    avgWholesale: 0,
                },
            );

            if (totalType === TotalTypes.AvgRetail) {
                plan = avgRetail / avgUnits;
            } else {
                plan = avgWholesale / avgUnits;
            }

            plan = safeParseFloat(plan, 0);
        } else {
            plan = filteredDoors.reduce((planValue, door) => {
                const planPath = buildPathBasedOnType(door, groupId).join(DIMENSION_IDS_DELIMITER);
                const doorObject = doors?.find((doorObj) => doorObj.id === door);
                const totalConversionRate = getTotalConversionRate(doorObject);
                const additionalPlanValue = amphibiousGetIn(reviewPlan, [planPath, "struct_value", totalType], "");
                if (totalType === TotalTypes.Skus) {
                    return Math.max(planValue, safeParseFloat(additionalPlanValue, 0));
                } else if (totalType === TotalTypes.Wholesale || totalType === TotalTypes.Retail) {
                    return planValue + safeParseFloat(additionalPlanValue / totalConversionRate, 0);
                } else {
                    return planValue + safeParseFloat(additionalPlanValue, 0);
                }
            }, 0);
        }
    }

    return plan;
};

export const formatValueOnPlanAndTotalType = ({
    value,
    totalType,
    planRowType,
    currency,
}: {
    value: number;
    totalType: TotalTypes;
    planRowType?: PlanMetricRows;
    currency: { symbol: string; decimal_places: number };
}): string => {
    let actualValueFormatted = String(value);

    if (value === undefined) {
        actualValueFormatted = "-";
    } else {
        try {
            let fixedDecimal = 0;
            if (totalType === TotalTypes.Depth) {
                fixedDecimal = 1;
            }
            const numberValueFormatted = planRowType && planRowType === PlanMetricRows.Diff ? value : Math.abs(value);
            const actualValue = +numberValueFormatted.toFixed(fixedDecimal);
            actualValueFormatted = formatNumber(actualValue);

            // If Diff and above 0, prepend +
            if (planRowType && planRowType === PlanMetricRows.Diff && actualValue > 0) {
                actualValueFormatted = `+${actualValueFormatted}`;
            }
        } catch (e) {
            actualValueFormatted = formatNumber(0);
        }

        if ((!planRowType || planRowType === PlanMetricRows.Total) && isCurrency(totalType)) {
            const symbol = get(currency, "symbol", "$");
            actualValueFormatted = symbol + actualValueFormatted;
        }
    }
    return actualValueFormatted;
};

export const getTotalConversionRate = (door: IDoor | undefined) => {
    return !isNaN(door?.totalConversionRate) ? Number(door?.totalConversionRate) : 1;
};

export const getDiffVariance = (base: number, value: number): string => {
    const diff = +(base - value).toFixed(1);
    const variance = diff === 0 ? 0 : base > 0 ? Math.abs(diff) / base : Number.POSITIVE_INFINITY;

    if (variance < 0.02) {
        return "great";
    } else if (variance >= 0.02 && variance < 0.05) {
        return "fair";
    } else if (variance >= 0.05 && variance <= 0.1) {
        return "off";
    } else if (variance > 0.1) {
        return "wayOff";
    }

    return "";
};

// TODO: Add UT
export const getDisplayPlansOrTotals = (nodeData: any): { displayTotals: boolean; displayPlanMetrics: boolean } => {
    // `data` will have `plan` if it's a plan
    const displayPlanMetrics = !!nodeData?.plan;
    // `data` won't have `plan`, it has `totalType`
    const displayTotals = !!nodeData?.totalType && !displayPlanMetrics;

    return {
        displayPlanMetrics,
        displayTotals,
    };
};
